/**
 * Contact form [data-contact-form]: POST JSON to /apps/public/how-can-help (app proxy).
 * Optional file → { fileName, fileBase64 }. Override URL: window.__contactHowCanHelpApiUrl
 * Max attachment (client): window.__contactHowCanHelpMaxFileBytes (default 5 MB). Files over this are blocked before upload.
 * If the server returns 413 after submit, the app proxy / API body limit is still too low—increase limits there (JSON+base64 is ~33% larger than the file).
 * On 413 or HTML error with an attachment, optional retry without file: window.__contactHowCanHelpRetryWithoutFile === true (default false).
 * Hidden: #submitted_from (`watkins_country` / localization-form when present; else Doubly / picker / Shopify.currency), #customer.
 * Strings: window.contactHowCanHelpStrings
 *
 * Form uses action="#" + onsubmit="return false" so a missing script cannot POST the page URL
 * (which often returns 500 HTML from Shopify).
 *
 * @class ContactHowCanHelpFormComponent
 * @class ContactHowCanHelpFormBinding
 */
const CONTACT_HOW_CAN_HELP_DEFAULT_API_URL = '/apps/public/how-can-help';

class ContactHowCanHelpFormComponent {
  static _documentSyncAttached = false;

  static get maxFileBytes() {
    return typeof window.__contactHowCanHelpMaxFileBytes === 'number' && window.__contactHowCanHelpMaxFileBytes > 0
      ? window.__contactHowCanHelpMaxFileBytes
      : 5 * 1024 * 1024;
  }

  /**
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  static str(key, fallback) {
    const S = window.contactHowCanHelpStrings || {};
    return S[key] || fallback;
  }

  static cleanStr(raw) {
    return String(raw == null ? '' : raw)
      .replace(/\u0000/g, '')
      .trim();
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} selector
   * @returns {string}
   */
  static fieldValue(form, selector) {
    const el = form.querySelector(selector);
    return el ? ContactHowCanHelpFormComponent.cleanStr(el.value) : '';
  }

  static normalizeCurrencyNode(v) {
    if (v == null || v === '') {
      return '';
    }
    if (typeof v === 'string' || typeof v === 'number') {
      return ContactHowCanHelpFormComponent.cleanStr(String(v));
    }
    if (typeof v === 'object') {
      const o = v;
      const keys = [
        'country',
        'countryName',
        'country_name',
        'currencyCountry',
        'name',
        'label',
        'currencyCode',
        'code',
        'currency',
        'iso',
        'isoCode',
      ];
      for (let i = 0; i < keys.length; i++) {
        const p = o[keys[i]];
        if (p != null && String(p).trim()) {
          return ContactHowCanHelpFormComponent.cleanStr(String(p));
        }
      }
    }
    return '';
  }

  static resolveFromDoublyGlobal(g) {
    if (!g || typeof g !== 'object') {
      return '';
    }
    const topKeys = [
      'currentCurrency',
      'selectedCurrency',
      'activeCurrency',
      'displayCurrency',
      'currency',
      'currencyCode',
      'country',
      'countryCode',
    ];
    for (let t = 0; t < topKeys.length; t++) {
      const s = ContactHowCanHelpFormComponent.normalizeCurrencyNode(g[topKeys[t]]);
      if (s) {
        return s;
      }
    }
    return '';
  }

  static resolveFromDoublyDom() {
    const el = document.querySelector('.custom-language-picker .current');
    if (!el) {
      return '';
    }
    return ContactHowCanHelpFormComponent.cleanStr(el.textContent || el.innerText || '');
  }

  static resolveSubmittedFrom() {
    if (typeof globalThis.getActiveWatkinsCountryForDetectedContent === 'function') {
      try {
        return globalThis.getActiveWatkinsCountryForDetectedContent();
      } catch {
        /* */
      }
    }
    const g = window.DoublyGlobalCurrency;
    const fromGlobal = ContactHowCanHelpFormComponent.resolveFromDoublyGlobal(g);
    if (fromGlobal) {
      return fromGlobal;
    }
    const fromDom = ContactHowCanHelpFormComponent.resolveFromDoublyDom();
    if (fromDom) {
      return fromDom;
    }
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return ContactHowCanHelpFormComponent.cleanStr(String(window.Shopify.currency.active));
    }
    return '';
  }

  /**
   * @param {HTMLFormElement} form
   */
  static syncSubmittedFromField(form) {
    const input = form.querySelector('#submitted_from');
    if (input) {
      input.value = ContactHowCanHelpFormComponent.resolveSubmittedFrom();
    }
  }

  static syncAllContactFormsSubmittedFrom() {
    const forms = document.querySelectorAll('form[data-contact-form]');
    for (let i = 0; i < forms.length; i++) {
      ContactHowCanHelpFormComponent.syncSubmittedFromField(forms[i]);
    }
  }

  /** Shopify customer id as a string (guest / missing → "-1"). */
  static customerPayloadValue(form) {
    const raw = ContactHowCanHelpFormComponent.fieldValue(form, '#customer');
    return raw === '' ? '-1' : raw;
  }

  static safeFileName(name) {
    let base = String(name || 'attachment').split(/[/\\]/).pop() || 'attachment';
    base = base.replace(/\u0000/g, '').replace(/[/\\]/g, '_').trim().slice(0, 180);
    return base || 'attachment';
  }

  /**
   * @param {File} file
   * @returns {Promise<{ fileName: string, fileBase64: string }>}
   */
  static readFileAsBase64Payload(file) {
    return new Promise(function (resolve, reject) {
      const reader = new FileReader();
      reader.onload = function () {
        const result = reader.result;
        const b64 =
          typeof result === 'string' && result.indexOf('base64,') !== -1
            ? result.split('base64,')[1] || ''
            : String(result || '');
        resolve({
          fileName: ContactHowCanHelpFormComponent.safeFileName(file.name),
          fileBase64: b64,
        });
      };
      reader.onerror = function () {
        reject(new Error('read'));
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * @param {HTMLFormElement} form
   * @param {'error'|'success'} type
   * @param {string} message
   */
  static showFeedback(form, type, message) {
    const errEl = form.querySelector('[data-contact-how-can-help-error]');
    const okEl = form.querySelector('[data-contact-how-can-help-success]');
    if (errEl) {
      errEl.classList.toggle('d-none', type !== 'error');
      errEl.textContent = type === 'error' ? message : '';
    }
    if (okEl) {
      okEl.classList.toggle('d-none', type !== 'success');
      okEl.textContent = type === 'success' ? message : '';
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  static clearFeedback(form) {
    const errEl = form.querySelector('[data-contact-how-can-help-error]');
    const okEl = form.querySelector('[data-contact-how-can-help-success]');
    if (errEl) {
      errEl.classList.add('d-none');
      errEl.textContent = '';
    }
    if (okEl) {
      okEl.classList.add('d-none');
      okEl.textContent = '';
    }
  }

  static isHtmlResponse(text) {
    const t = String(text || '').trim().slice(0, 64).toLowerCase();
    return t.indexOf('<!doctype') === 0 || t.indexOf('<html') === 0;
  }

  /**
   * @param {string} bodyText
   * @param {number} status
   * @returns {string}
   */
  static parseErrorMessage(bodyText, status) {
    const C = ContactHowCanHelpFormComponent;
    if (status === 413) {
      return C.str(
        'payloadTooLarge',
        'The attachment is too large for our server. Try a smaller file or submit without a file.'
      );
    }
    if (!bodyText) return '';
    if (C.isHtmlResponse(bodyText)) {
      return C.str(
        'serverError',
        'The server could not process your request. Try again without an attachment or contact support.'
      );
    }
    try {
      const j = JSON.parse(bodyText);
      if (j && typeof j === 'object') {
        if (j.message != null && String(j.message).trim()) return String(j.message).trim();
        if (j.error != null && String(j.error).trim()) return String(j.error).trim();
        if (j.detail != null && String(j.detail).trim()) return String(j.detail).trim();
        if (j.reason != null && String(j.reason).trim()) return String(j.reason).trim();
        if (Array.isArray(j.errors) && j.errors.length && j.errors[0] != null) {
          return String(j.errors[0]);
        }
        if (j.errors && typeof j.errors === 'object' && !Array.isArray(j.errors)) {
          const keys = Object.keys(j.errors);
          if (keys.length && Array.isArray(j.errors[keys[0]]) && j.errors[keys[0]].length) {
            return String(j.errors[keys[0]][0]);
          }
        }
      }
    } catch (e) {
      /* plain text */
    }
    return bodyText.length > 280 ? bodyText.slice(0, 280) + '…' : bodyText;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {Record<string, unknown>}
   */
  static buildBasePayload(form) {
    const C = ContactHowCanHelpFormComponent;
    return {
      firstName: C.fieldValue(form, '#ContactForm-first_name'),
      lastName: C.fieldValue(form, '#ContactForm-last_name'),
      email: C.fieldValue(form, '#ContactForm-email'),
      phone: C.fieldValue(form, '#ContactForm-phone'),
      subject: C.fieldValue(form, '#subject'),
      idNumber: C.fieldValue(form, '#ContactForm-id_number'),
      orderNumber: C.fieldValue(form, '#ContactForm-order_number'),
      message: C.fieldValue(form, '#ContactForm-body'),
      submitted_from: C.fieldValue(form, '#submitted_from'),
      customer: C.customerPayloadValue(form),
    };
  }

  /**
   * @param {string} apiUrl
   * @param {Record<string, unknown>} payload
   * @returns {Promise<{ ok: boolean, status: number, body: string }>}
   */
  static postContactJson(apiUrl, payload) {
    let body;
    try {
      body = JSON.stringify(payload);
    } catch (err) {
      return Promise.reject(new Error('stringify'));
    }
    return fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Accept: 'application/json, text/plain, */*',
      },
      credentials: 'same-origin',
      body: body,
    }).then(function (r) {
      return r.text().then(function (t) {
        return { ok: r.ok, status: r.status, body: t };
      });
    });
  }

  /**
   * @param {{ ok: boolean, status: number, body: string }} res
   * @param {boolean} hadFile
   * @returns {boolean}
   */
  static shouldRetryWithoutFile(res, hadFile) {
    return (
      window.__contactHowCanHelpRetryWithoutFile === true &&
      hadFile &&
      !res.ok &&
      (res.status === 413 || ContactHowCanHelpFormComponent.isHtmlResponse(res.body))
    );
  }

  static attachDocumentSyncListeners() {
    if (ContactHowCanHelpFormComponent._documentSyncAttached) {
      return;
    }
    ContactHowCanHelpFormComponent._documentSyncAttached = true;
    const C = ContactHowCanHelpFormComponent;
    window.addEventListener('load', C.syncAllContactFormsSubmittedFrom);
    const delays = [0, 200, 800, 2000];
    for (let d = 0; d < delays.length; d++) {
      setTimeout(C.syncAllContactFormsSubmittedFrom, delays[d]);
    }
    document.addEventListener(
      'click',
      function (e) {
        if (e.target && e.target.closest && e.target.closest('.custom-language-picker')) {
          setTimeout(C.syncAllContactFormsSubmittedFrom, 0);
          setTimeout(C.syncAllContactFormsSubmittedFrom, 200);
        }
      },
      true
    );
  }

  static init() {
    if (window.__watkinsContactHowCanHelpLoaded) {
      return;
    }
    window.__watkinsContactHowCanHelpLoaded = true;

    const boot = function () {
      ContactHowCanHelpFormComponent.attachDocumentSyncListeners();
      document.querySelectorAll('form[data-contact-form]').forEach(function (form) {
        if (form.__watkinsContactHowCanHelpBound) {
          return;
        }
        form.__watkinsContactHowCanHelpBound = true;
        new ContactHowCanHelpFormBinding(form).attach();
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

class ContactHowCanHelpFormBinding {
  /**
   * @param {HTMLFormElement} form
   */
  constructor(form) {
    this.form = form;
    this._onSubmit = this.handleSubmit.bind(this);
  }

  attach() {
    this.form.addEventListener('submit', this._onSubmit, true);
  }

  /**
   * @param {SubmitEvent} e
   */
  handleSubmit(e) {
    const form = e.target;
    if (!form || form !== this.form || !form.matches || !form.matches('[data-contact-form]')) {
      return;
    }

    const C = ContactHowCanHelpFormComponent;

    e.preventDefault();
    e.stopPropagation();
    C.clearFeedback(form);
    C.syncSubmittedFromField(form);

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const fileInput = form.querySelector('#ContactForm-file');
    const file = fileInput && fileInput.files && fileInput.files[0];

    if (file && file.size > C.maxFileBytes) {
      C.showFeedback(
        form,
        'error',
        C.str('fileTooLarge', 'File is too large. Please choose a smaller file.')
      );
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    Utility.setSubmitButtonLoading(submitBtn, true);

    const apiUrl = window.__contactHowCanHelpApiUrl || CONTACT_HOW_CAN_HELP_DEFAULT_API_URL;
    const basePayload = C.buildBasePayload(form);

    const filePromise = file ? C.readFileAsBase64Payload(file) : Promise.resolve(null);

    filePromise
      .then(function (filePart) {
        const payload = Object.assign({}, basePayload);
        let hadFile = false;
        if (filePart && filePart.fileBase64) {
          hadFile = true;
          payload.file = {
            fileName: filePart.fileName,
            fileBase64: filePart.fileBase64,
          };
        }
        return C.postContactJson(apiUrl, payload).then(function (res) {
          return { res: res, hadFile: hadFile };
        });
      })
      .then(function (ctx) {
        const res = ctx.res;
        const hadFile = ctx.hadFile;
        if (res.ok) {
          C.showFeedback(
            form,
            'success',
            C.str('submitSuccess', "Thanks for contacting us. We'll get back to you as soon as possible.")
          );
          form.reset();
          return;
        }
        if (C.shouldRetryWithoutFile(res, hadFile)) {
          return C.postContactJson(apiUrl, basePayload).then(function (res2) {
            if (res2.ok) {
              C.showFeedback(
                form,
                'success',
                C.str('submitSuccess', "Thanks for contacting us. We'll get back to you as soon as possible.") +
                  ' ' +
                  C.str(
                    'attachmentNotSent',
                    'Your message was sent without the file. Use a smaller attachment next time or send it by email.'
                  )
              );
              form.reset();
              return;
            }
            const parsed2 = C.parseErrorMessage(res2.body, res2.status);
            const msg2 =
              parsed2 ||
              C.str('submitError', 'Something went wrong. Please try again.') + ' (' + res2.status + ')';
            C.showFeedback(form, 'error', msg2);
          });
        }
        const parsed = C.parseErrorMessage(res.body, res.status);
        const msg =
          parsed || C.str('submitError', 'Something went wrong. Please try again.') + ' (' + res.status + ')';
        C.showFeedback(form, 'error', msg);
      })
      .catch(function (err) {
        if (err && err.message === 'stringify') {
          C.showFeedback(
            form,
            'error',
            C.str('submitError', 'Could not prepare your message. Please shorten text and try again.')
          );
        } else {
          C.showFeedback(form, 'error', C.str('networkError', 'Network error. Please try again later.'));
        }
      })
      .finally(function () {
        Utility.setSubmitButtonLoading(submitBtn, false);
      });
  }
}

globalThis.ContactHowCanHelpFormComponent = ContactHowCanHelpFormComponent;
ContactHowCanHelpFormComponent.init();
